import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  PlusCircleIcon,
  DocumentArrowDownIcon,
  ClipboardDocumentListIcon,
  BuildingLibraryIcon,
  BookOpenIcon,
  BeakerIcon,
  ArrowRightIcon,
  DocumentCheckIcon,
} from "@heroicons/react/24/outline";
import { useAppContext } from "../../functions/ContextAPI";
import { getProgrammeTypeDisplayName, calculateAccreditationRecommendation } from "../../utils/accreditationRules";
import { getCompletionStatus } from "../../service/templateService";
import { LoadingSpinner } from "../../components/LoadingStates";
import { generateAccreditationPDF } from "../../utils/pdfGenerator";

/**
 * Results Page Component
 * Displays comprehensive submission results with scores and recommendation
 * Uses /nuc_scoring/scores/status/:id endpoint for data
 */
const Results = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const context = useAppContext();
  const [showAnimation, setShowAnimation] = useState(false);
  const [submissionData, setSubmissionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get all programmes from context
  const allProgrammes = context.programmes || [];
  const selectedProgramme = context.selectedProgramme;

  // Get accreditationId from URL params, context, or location state
  const accreditationId =
    params.accreditationId ||
    selectedProgramme?.accreditationId ||
    selectedProgramme?._id ||
    location.state?.accreditationId;

  // Fetch submission data on mount
  useEffect(() => {
    const fetchSubmissionData = async () => {
      if (!accreditationId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Fetch completion status directly from /nuc_scoring/scores/status/:id
        const statusData = await getCompletionStatus(accreditationId);

        console.log('[Results] Raw statusData:', statusData);
        console.log('[Results] statusData.templateStatus:', statusData?.templateStatus);
        console.log('[Results] statusData.overallStatus:', statusData?.overallStatus);

        // Calculate total score and max possible score from templateStatus
        let totalScore = 0;
        let maxPossibleScore = 0;
        const templates = statusData?.templateStatus || [];

        console.log('[Results] Templates array:', templates);
        console.log('[Results] Templates length:', templates.length);

        // Build templateScores object and templates array for calculateAccreditationRecommendation
        const templateScores = {};
        const templatesArray = [];

        templates.forEach(template => {
          console.log('[Results] Processing template:', template.templateName, 'Score:', template.totalScore, 'Max:', template.maxPossibleScore);
          totalScore += template.totalScore || 0;
          maxPossibleScore += template.maxPossibleScore || 0;

          // Build templateScores object (format expected by calculateAccreditationRecommendation)
          templateScores[template.templateId] = {
            metadata: {
              totalScore: template.totalScore || 0,
              maxPossibleScore: template.maxPossibleScore || 0,
            }
          };

          // Build templates array with required fields
          templatesArray.push({
            _id: template.templateId,
            name: template.templateName,
            type: template.templateType,
          });
        });

        console.log('[Results] Final calculation:', { totalScore, maxPossibleScore });
        console.log('[Results] TemplateScores:', templateScores);
        console.log('[Results] Templates array for rules:', templatesArray);

        // Calculate percentage
        const percentage = maxPossibleScore > 0 ? (totalScore / maxPossibleScore) * 100 : 0;

        // Use the same calculation as Dashboard - proper core area rules
        const programmeTypeForCalc = selectedProgramme?.programmeType || statusData?.programmeMode || "undergraduate";
        const accreditationResult = calculateAccreditationRecommendation(
          templateScores,
          templatesArray,
          programmeTypeForCalc
        );

        const recommendation = accreditationResult.recommendation || "";
        const recommendationReason = accreditationResult.reason || "No scores available for evaluation.";
        const coreAreas = accreditationResult.details?.coreAreas || [];

        console.log('[Results] Accreditation result:', accreditationResult);

        // Transform template data for display
        const transformedTemplates = templates.map(template => ({
          _id: template.templateId,
          templateId: template.templateId,
          name: template.templateName,
          templateName: template.templateName,
          type: template.templateType,
          totalScore: template.totalScore || 0,
          maxPossibleScore: template.maxPossibleScore || 0,
          status: template.status,
          completionStatus: template.completionStatus,
          itemsCompleted: template.itemsCompleted || 0,
          itemsCount: template.itemsCount || 0,
          submittedAt: template.submittedAt,
        }));

        // Build final data structure
        const transformedData = {
          totalScore,
          maxPossibleScore,
          percentage,
          recommendation,
          recommendationReason,
          coreAreas, // Include core areas from calculation
          accreditationDetails: accreditationResult.details, // Include full details
          programmeDetails: {
            programmeName: selectedProgramme?.programmeName || "N/A",
            institutionName: selectedProgramme?.institutionName || "N/A",
            faculty: selectedProgramme?.faculty || selectedProgramme?.facultyName || "N/A",
            department: selectedProgramme?.department || selectedProgramme?.departmentName || "N/A",
          },
          programmeType: selectedProgramme?.programmeType || statusData?.programmeMode || "undergraduate",
          programmeMode: statusData?.programmeMode || "undergraduate",
          submittedAt: templates.find(t => t.submittedAt)?.submittedAt || null,
          templates: transformedTemplates,
          overallStatus: statusData?.overallStatus || {},
          stage: statusData?.stage || selectedProgramme?.accreditationData?.stage, // Include stage
          hasSubmitted: statusData?.overallStatus?.isSubmitted === true,
        };

        console.log('[Results] Transformed data:', transformedData);

        setSubmissionData(transformedData);
      } catch (err) {
        console.error("Error fetching submission data:", err);
        setError(err.message || "Failed to load submission data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubmissionData();
  }, [accreditationId, selectedProgramme]);

  // Trigger animation after component mounts
  useEffect(() => {
    setTimeout(() => setShowAnimation(true), 100);
  }, []);

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <LoadingSpinner text="Loading submission data..." size={60} />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-600 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-red-900 mb-2">Error Loading Data</h3>
            <p className="text-red-800 mb-4">{error}</p>
            <button
              onClick={() => navigate("/dashboard")}
              className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If no submission data, show programme list with no results state
  // Check if we should show results (either submitted OR stage 6)
  const isStage6 = submissionData?.stage === 6 || selectedProgramme?.accreditationData?.stage === 6;
  const shouldShowResults = submissionData && (submissionData.hasSubmitted || isStage6);

  console.log('[Results] Checking display conditions:', {
    hasSubmissionData: !!submissionData,
    hasSubmitted: submissionData?.hasSubmitted,
    stage: submissionData?.stage,
    isStage6,
    shouldShowResults,
    totalScore: submissionData?.totalScore,
    templatesCount: submissionData?.templates?.length,
  });

  if (!shouldShowResults) {
    console.log('[Results] Showing NoResultsView because:', {
      noData: !submissionData,
      notSubmitted: !submissionData?.hasSubmitted,
      notStage6: !isStage6,
    });
    return <NoResultsView
      programmes={allProgrammes}
      navigate={navigate}
      showAnimation={showAnimation}
    />;
  }

  console.log('[Results] Rendering results page with data');

  const {
    totalScore,
    recommendation,
    recommendationReason,
    programmeDetails,
    programmeType,
    submittedAt,
    templates,
    percentage,
    maxPossibleScore,
  } = submissionData;

  const totalPercentage = percentage || 0;
  const programmeTypeDisplay = getProgrammeTypeDisplayName(programmeType || "UG");

  // Get status configuration based on recommendation
  const getStatusConfig = () => {
    if (!recommendation) {
      return {
        icon: ExclamationTriangleIcon,
        iconColor: "text-gray-500",
        bgGradient: "from-gray-50 to-gray-100",
        borderColor: "border-gray-300",
        textColor: "text-gray-700",
        badgeColor: "bg-gray-100 text-gray-800 border-gray-200",
        status: "PENDING",
      };
    }

    const lowerRec = recommendation.toLowerCase();
    if (lowerRec.includes("full") || (lowerRec.includes("accredited") && !lowerRec.includes("not"))) {
      return {
        icon: CheckCircleIcon,
        iconColor: "text-green-500",
        bgGradient: "from-green-50 to-green-100",
        borderColor: "border-green-300",
        textColor: "text-green-900",
        badgeColor: "bg-green-100 text-green-800 border-green-200",
        status: recommendation.toUpperCase(),
      };
    } else if (lowerRec.includes("interim")) {
      return {
        icon: ExclamationTriangleIcon,
        iconColor: "text-yellow-500",
        bgGradient: "from-yellow-50 to-yellow-100",
        borderColor: "border-yellow-300",
        textColor: "text-yellow-900",
        badgeColor: "bg-yellow-100 text-yellow-800 border-yellow-200",
        status: "INTERIM ACCREDITATION",
      };
    } else {
      return {
        icon: XCircleIcon,
        iconColor: "text-red-500",
        bgGradient: "from-red-50 to-red-100",
        borderColor: "border-red-300",
        textColor: "text-red-900",
        badgeColor: "bg-red-100 text-red-800 border-red-200",
        status: recommendation.toUpperCase(),
      };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  const handleDownloadReport = () => {
    // Prepare panel members and representative for signatures
    const panelMembers = selectedProgramme?.accreditationData?.panel || [];
    const representative = context.user;

    // Generate PDF report
    generateAccreditationPDF({
      programmeDetails,
      recommendation,
      recommendationReason,
      totalScore,
      maxPossibleScore,
      percentage: totalPercentage,
      templates,
      submittedAt,
      isStage6,
      programmeType: programmeTypeDisplay,
      panelMembers,
      representative,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Success Header with Animation */}
        <div
          className={`transition-all duration-700 transform ${
            showAnimation ? "translate-y-0 opacity-100" : "-translate-y-10 opacity-0"
          }`}
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-green-600 shadow-2xl mb-6 animate-bounce">
              <CheckCircleIcon className="h-14 w-14 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-3">
              Submission Successful!
            </h1>
            <p className="text-lg text-gray-600">
              Your accreditation scores have been submitted and recorded.
            </p>
            {submittedAt && (
              <p className="text-sm text-gray-500 mt-2">
                Submitted on {new Date(submittedAt).toLocaleString()}
              </p>
            )}
            {/* Stage 6 Badge */}
            {isStage6 && (
              <div className="mt-4 inline-flex items-center px-4 py-2 rounded-full bg-green-100 border-2 border-green-300">
                <DocumentCheckIcon className="h-5 w-5 text-green-700 mr-2" />
                <span className="text-sm font-semibold text-green-800">
                  Stage 6: Submitted for Review
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Programme Details Card */}
        <div
          className={`transition-all duration-700 delay-100 transform ${
            showAnimation ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="bg-white rounded-xl shadow-lg border-2 border-blue-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
              <div className="flex items-center space-x-3">
                <AcademicCapIcon className="h-7 w-7 text-white" />
                <h2 className="text-xl font-bold text-white">Programme Details</h2>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Programme Name</p>
                  <p className="text-base font-semibold text-gray-900 mt-1">
                    {programmeDetails?.programmeName || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-medium">Institution</p>
                  <p className="text-base font-semibold text-gray-900 mt-1">
                    {programmeDetails?.institutionName || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-medium">Faculty</p>
                  <p className="text-base font-semibold text-gray-900 mt-1">
                    {programmeDetails?.faculty || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-medium">Department</p>
                  <p className="text-base font-semibold text-gray-900 mt-1">
                    {programmeDetails?.department || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 font-medium">Programme Type</p>
                  <p className="text-base font-semibold text-blue-700 mt-1">
                    {programmeTypeDisplay}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Total Score Card */}
        <div
          className={`transition-all duration-700 delay-200 transform ${
            showAnimation ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className={`bg-gradient-to-br ${config.bgGradient} rounded-xl border-2 ${config.borderColor} p-8 shadow-lg`}>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white shadow-lg mb-4">
                <Icon className={`h-12 w-12 ${config.iconColor}`} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Total Score</h3>
              <div className="text-7xl font-bold text-gray-900 mb-2">
                {totalScore.toFixed(2)}
                <span className="text-3xl text-gray-600">/{maxPossibleScore.toFixed(2)}</span>
              </div>
              <div className="text-4xl font-semibold text-blue-700 mb-4">
                {totalPercentage.toFixed(2)}%
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-white bg-opacity-60 rounded-full h-4 mb-4 overflow-hidden">
                <div
                  className="h-4 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${totalPercentage}%` }}
                />
              </div>

              {/* Recommendation Badge */}
              <div className="inline-flex items-center space-x-2">
                <span className={`px-6 py-3 rounded-full text-base font-bold border-2 ${config.badgeColor}`}>
                  {config.status}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Scores Card - Template-Based */}
        <div
          className={`transition-all duration-700 delay-300 transform ${
            showAnimation ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-700 to-gray-800 px-6 py-4">
              <div className="flex items-center space-x-3">
                <DocumentTextIcon className="h-7 w-7 text-white" />
                <h2 className="text-xl font-bold text-white">Template Scores</h2>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {templates && templates.length > 0 ? (
                  templates.map((template, index) => {
                    const templateScore = template.totalScore || 0;
                    const templateMax = template.maxPossibleScore || 0;
                    const percentage = templateMax > 0 ? ((templateScore / templateMax) * 100).toFixed(1) : 0;
                    const isCoreTemplate = template.type === "core";
                    const isSubmitted = template.submittedAt !== null && template.submittedAt !== undefined;
                    const itemsProgress = `${template.itemsCompleted || 0}/${template.itemsCount || 0}`;

                    return (
                      <div
                        key={template._id || template.templateId || index}
                        className={`p-4 rounded-lg border-2 ${
                          isCoreTemplate
                            ? "bg-blue-50 border-blue-200"
                            : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className={`text-base font-semibold ${
                                isCoreTemplate ? "text-blue-900" : "text-gray-900"
                              }`}>
                                {template.name || template.templateName || "Unnamed Template"}
                              </span>
                              {isCoreTemplate && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-600 text-white">
                                  CORE
                                </span>
                              )}
                              {isSubmitted && (
                                <CheckCircleIcon className="h-5 w-5 text-green-600" />
                              )}
                            </div>
                            <div className="text-xs text-gray-600">
                              Items completed: <span className="font-medium">{itemsProgress}</span>
                              {template.completionStatus && (
                                <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                                  template.completionStatus === 'complete'
                                    ? 'bg-green-100 text-green-800'
                                    : template.completionStatus === 'partial'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {template.completionStatus}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="ml-6 text-right min-w-[140px]">
                            <div className={`text-2xl font-bold ${
                              isCoreTemplate ? "text-blue-700" : "text-gray-700"
                            }`}>
                              {templateScore.toFixed(2)}/{templateMax.toFixed(2)}
                            </div>
                            <div className="text-sm font-semibold text-gray-600">
                              ({percentage}%)
                            </div>
                          </div>
                        </div>
                        <div className="w-full bg-white rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-1000 ${
                              isCoreTemplate
                                ? "bg-gradient-to-r from-blue-500 to-blue-600"
                                : "bg-gradient-to-r from-gray-400 to-gray-500"
                            }`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-center text-gray-500 py-8">No template data available</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Overall Statistics Card */}
        {submissionData?.overallStatus && (
          <div
            className={`transition-all duration-700 delay-400 transform ${
              showAnimation ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
            }`}
          >
            <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-4">
                <div className="flex items-center space-x-3">
                  <ClipboardDocumentListIcon className="h-7 w-7 text-white" />
                  <h2 className="text-xl font-bold text-white">Submission Statistics</h2>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-sm text-blue-600 font-medium mb-1">Core Templates</p>
                    <p className="text-2xl font-bold text-blue-900">
                      {submissionData.overallStatus.coreCompleted || 0}/{submissionData.overallStatus.coreTemplates || 0}
                    </p>
                    <div className="mt-2 w-full bg-blue-200 rounded-full h-2">
                      <div
                        className="h-2 bg-blue-600 rounded-full transition-all duration-500"
                        style={{ width: `${submissionData.overallStatus.coreCompletionPercentage || 0}%` }}
                      />
                    </div>
                    <p className="text-xs text-blue-700 mt-1">
                      {submissionData.overallStatus.coreCompletionPercentage || 0}% complete
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-sm text-gray-600 font-medium mb-1">Supplementary Templates</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {submissionData.overallStatus.supplementaryCompleted || 0}/{submissionData.overallStatus.supplementaryTemplates || 0}
                    </p>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="h-2 bg-gray-600 rounded-full transition-all duration-500"
                        style={{ width: `${submissionData.overallStatus.supplementaryCompletionPercentage || 0}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-700 mt-1">
                      {submissionData.overallStatus.supplementaryCompletionPercentage || 0}% complete
                    </p>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <p className="text-sm text-green-600 font-medium mb-1">Overall Progress</p>
                    <p className="text-2xl font-bold text-green-900">
                      {submissionData.overallStatus.overallCompleted || 0}/{submissionData.overallStatus.totalTemplates || 0}
                    </p>
                    <div className="mt-2 w-full bg-green-200 rounded-full h-2">
                      <div
                        className="h-2 bg-green-600 rounded-full transition-all duration-500"
                        style={{ width: `${submissionData.overallStatus.overallCompletionPercentage || 0}%` }}
                      />
                    </div>
                    <p className="text-xs text-green-700 mt-1">
                      {submissionData.overallStatus.overallCompletionPercentage || 0}% complete
                    </p>
                  </div>
                </div>

                {submissionData.overallStatus.isSubmitted && (
                  <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center space-x-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <p className="text-sm text-green-800 font-medium">
                      All scores have been successfully submitted for review
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Recommendation Details Card */}
        {recommendation && (
          <div
            className={`transition-all duration-700 delay-500 transform ${
              showAnimation ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
            }`}
          >
            <div className={`bg-gradient-to-br ${config.bgGradient} rounded-xl border-2 ${config.borderColor} overflow-hidden shadow-lg`}>
              <div className="px-6 py-4 border-b-2 border-current border-opacity-20">
                <h3 className="text-lg font-bold text-gray-900">
                  Final Accreditation Recommendation
                </h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Icon className={`h-6 w-6 ${config.iconColor} flex-shrink-0 mt-1`} />
                    <div>
                      <p className="text-base font-semibold text-gray-900 mb-2">
                        Recommendation: <span className="text-lg uppercase">{config.status}</span>
                      </p>
                      <p className={`text-sm ${config.textColor} leading-relaxed`}>
                        {recommendationReason ||
                          "Based on the submitted scores and NUC accreditation guidelines, this programme has been evaluated and the recommendation has been determined."}
                      </p>
                    </div>
                  </div>
                  <div className="bg-white bg-opacity-60 rounded-lg p-4 border border-gray-300">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">Note:</span> This recommendation is based on
                      the scores submitted for {programmeTypeDisplay} programme evaluation following
                      NUC accreditation guidelines and criteria.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div
          className={`transition-all duration-700 delay-600 transform ${
            showAnimation ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 p-6">
            <div className="flex flex-col sm:flex-row items-center justify-center">
              <button
                onClick={handleDownloadReport}
                className="w-full sm:w-auto px-6 py-3 text-sm font-medium text-blue-700 bg-blue-50 border-2 border-blue-300 rounded-lg hover:bg-blue-100 hover:border-blue-400 transition-all duration-200 flex items-center justify-center space-x-2 shadow-sm hover:shadow-md"
              >
                <DocumentArrowDownIcon className="h-5 w-5" />
                <span>Download Report</span>
              </button>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center text-sm text-gray-500 py-4">
          <p>
            This submission has been recorded in the NUC accreditation system.
            For any questions or concerns, please contact your administrator.
          </p>
        </div>

        {/* All Programmes Section */}
        {allProgrammes.length > 0 && (
          <ProgrammeListSection
            programmes={allProgrammes}
            submittedProgrammeId={programmeDetails?.programmeId}
            navigate={navigate}
            showAnimation={showAnimation}
          />
        )}
      </div>
    </div>
  );
};

/**
 * No Results View Component
 * Shown when there are no submission results yet
 */
const NoResultsView = ({ programmes, navigate, showAnimation }) => {
  const handleGoToDashboard = () => {
    navigate("/dashboard");
  };

  const handleSelectProgramme = (programme) => {
    // Navigate to dashboard with programme selected
    navigate("/dashboard", { state: { selectedProgramme: programme } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* No Submissions Header */}
        <div
          className={`transition-all duration-700 transform ${
            showAnimation ? "translate-y-0 opacity-100" : "-translate-y-10 opacity-0"
          }`}
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 shadow-lg mb-6">
              <ClipboardDocumentListIcon className="h-14 w-14 text-blue-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-3">
              No Submissions Yet
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              You haven't submitted any accreditation scores yet. Select a programme below to begin your evaluation.
            </p>
          </div>
        </div>

        {/* Info Card */}
        <div
          className={`transition-all duration-700 delay-100 transform ${
            showAnimation ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border-2 border-blue-200 p-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Getting Started
                </h3>
                <p className="text-sm text-gray-700 mb-3">
                  To submit accreditation scores, follow these steps:
                </p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
                  <li>Select a programme from the list below</li>
                  <li>Review the programme details and accreditation criteria</li>
                  <li>Enter scores for all evaluation areas</li>
                  <li>Submit your evaluation for processing</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* Programmes List */}
        {programmes.length > 0 ? (
          <div
            className={`transition-all duration-700 delay-200 transform ${
              showAnimation ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
            }`}
          >
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Available Programmes
              </h2>
              <p className="text-gray-600">
                Select a programme to begin the accreditation evaluation process.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
              {programmes.map((programme, index) => (
                <ProgrammeCard
                  key={programme._id || programme.id || index}
                  programme={programme}
                  onSelect={handleSelectProgramme}
                  delay={index * 50}
                />
              ))}
            </div>
          </div>
        ) : (
          <div
            className={`transition-all duration-700 delay-200 transform ${
              showAnimation ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
            }`}
          >
            <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 p-12 text-center">
              <BuildingLibraryIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No Programmes Found
              </h3>
              <p className="text-gray-600 mb-6">
                You don't have any programmes assigned to you. Please contact NUC administration.
              </p>
            </div>
          </div>
        )}

        {/* Action Button */}
        <div
          className={`transition-all duration-700 delay-300 transform ${
            showAnimation ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="flex justify-center">
            <button
              onClick={handleGoToDashboard}
              className="px-8 py-4 text-base font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-3"
            >
              <span>Go to Dashboard</span>
              <ArrowRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Programme List Section Component
 * Shows all programmes with submission status
 */
const ProgrammeListSection = ({ programmes, submittedProgrammeId, navigate, showAnimation }) => {
  const handleSelectProgramme = (programme) => {
    navigate("/dashboard", { state: { selectedProgramme: programme } });
  };

  return (
    <>
      {/* Separator */}
      <div
        className={`transition-all duration-700 delay-600 transform ${
          showAnimation ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
        }`}
      >
        <div className="relative">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t-2 border-gray-300" />
          </div>
          <div className="relative flex justify-center">
            <span className="px-4 bg-gradient-to-br from-blue-50 via-white to-blue-50 text-sm font-semibold text-gray-700">
              All Programmes
            </span>
          </div>
        </div>
      </div>

      {/* Programmes Grid */}
      <div
        className={`transition-all duration-700 delay-700 transform ${
          showAnimation ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
        }`}
      >
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Your Programmes
          </h2>
          <p className="text-gray-600">
            Submit scores for other programmes or view details.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          {programmes.map((programme, index) => {
            const programmeId = programme._id || programme.id;
            const isSubmitted = programmeId === submittedProgrammeId;

            return (
              <ProgrammeCard
                key={programmeId || index}
                programme={programme}
                onSelect={handleSelectProgramme}
                isSubmitted={isSubmitted}
                delay={index * 50}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};

/**
 * Programme Card Component
 * Displays individual programme information
 */
const ProgrammeCard = ({ programme, onSelect, isSubmitted = false, delay = 0 }) => {
  const programmeType = getProgrammeTypeDisplayName(programme.programmeType || "UG");

  const getProgrammeIcon = () => {
    const type = (programme.programmeType || "").toUpperCase();
    if (type.includes("PG") || type.includes("POST")) {
      return BeakerIcon;
    } else if (type.includes("ODL")) {
      return BookOpenIcon;
    }
    return AcademicCapIcon;
  };

  const Icon = getProgrammeIcon();

  return (
    <div className="bg-white rounded-xl shadow-md border-2 border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-200">
      <div className="p-6">
        {/* Header with Icon and Status */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <Icon className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 leading-tight">
                {programme.programmeName || programme.name || "Unnamed Programme"}
              </h3>
            </div>
          </div>
          {isSubmitted && (
            <div className="flex-shrink-0">
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                <CheckCircleIcon className="h-4 w-4 mr-1" />
                Submitted
              </div>
            </div>
          )}
        </div>

        {/* Programme Details */}
        <div className="space-y-2 mb-4">
          {programme.institutionName && (
            <div className="flex items-start space-x-2">
              <BuildingLibraryIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700 leading-tight">
                {programme.institutionName}
              </span>
            </div>
          )}

          {programme.facultyName && (
            <div className="flex items-start space-x-2">
              <BookOpenIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700 leading-tight">
                {programme.facultyName}
              </span>
            </div>
          )}

          {programme.departmentName && (
            <div className="flex items-start space-x-2">
              <ClipboardDocumentListIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700 leading-tight">
                {programme.departmentName}
              </span>
            </div>
          )}

          {programme.programmeType && (
            <div className="pt-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                {programmeType}
              </span>
            </div>
          )}
        </div>

        {/* Action Button */}
        <div className="pt-4 border-t border-gray-200">
          <button
            onClick={() => onSelect(programme)}
            className={`w-full px-4 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 ${
              isSubmitted
                ? "text-blue-700 bg-blue-50 border-2 border-blue-200 hover:bg-blue-100 hover:border-blue-300"
                : "text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-md hover:shadow-lg"
            }`}
          >
            <span>{isSubmitted ? "View Details" : "Submit Scores"}</span>
            <ArrowRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Results;
